import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { uniqBy } from 'lodash';
import printPdf from 'utils/printPdf';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import List from '@material-ui/core/List';

import { Scrollbars } from 'react-custom-scrollbars';
import IconButton from 'components/common/IconButton';
import IconPrint from 'components/icons/IconPrint';

import { submit, reset, getFormValues } from 'redux-form';

import Section, {
  SectionHeader, SectionTitle, SectionBody, SectionFooter, SectionBodyEmptyContent,
} from 'components/common/Section';
import Tooltip from 'components/common/Tooltip';
import Link from 'components/common/Link';
import IconBlurb from 'components/icons/IconBlurb';

import isDescendant from 'utils/isDescendant';
import hexToRgb from 'styles/hexToRgb';

import patientNotesToPDF from 'utils/patientNotesToPDF';

import NotesItem from './NotesItem';
import NotesTooltipContent from './NotesTooltipContent';
import NotesFilterForm from './NotesFilterForm';

const styles = theme => ({
  notesForm: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  notesFilterForm: {
    padding: '12px 24px 0 24px',
  },
  iconPaperPlane: {
    width: 19,
    height: 19,
    marginRight: 5,
  },
  button: {
    marginLeft: 20,
  },
  sectionBodyTrimmed: {
    paddingBottom: 0,
  },
  list: {
    padding: '8px 24px 0 24px',
  },
  iconBlurb: {
    width: 18,
    height: 17,
    color: theme.palette.primary[500],
  },
  iconPrint: {
    width: 22,
    height: 20,
    color: theme.palette.primary[500],
  },
  notesTooltip: {
    margin: '-8px -10px -8px 0',
  },
  buttonNotesActive: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.common.black)}, 0.08)`,
  },
  iconButton: {
    marginTop: -16,
    marginBottom: -16,
  },
});

class Notes extends Component {
  constructor(props) {
    super(props);

    this.state = {
      notesTooltipHovered: false,
      notesTooltipClicked: false,
      trimmedView: props.patient
        && props.patient['Patient Global Notes']
        && props.patient['Patient Global Notes'].length > 3,
    };

    this.onViewAllButtonClick = this.onViewAllButtonClick.bind(this);
    this.onNotesButtonMouseEnter = this.onNotesButtonMouseEnter.bind(this);
    this.onNotesTooltipMouseEnter = this.onNotesTooltipMouseEnter.bind(this);
    this.onNotesButtonFocus = this.onNotesButtonFocus.bind(this);
    this.onNotesTooltipMouseLeave = this.onNotesTooltipMouseLeave.bind(this);
    this.onNotesButtonMouseLeave = this.onNotesButtonMouseLeave.bind(this);
    this.onOrderNotesSubmitSuccess = this.onOrderNotesSubmitSuccess.bind(this);
    this.onOrderNotesSubmitButtonBlur = this.onOrderNotesSubmitButtonBlur.bind(this);
    this.onNotesButtonClick = this.onNotesButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onPrintButtonClick = this.onPrintButtonClick.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.trimmedView
      && nextProps.patient['Patient Global Notes'].length
      !== this.props.patient['Patient Global Notes']) {
      this.setState({
        trimmedView: nextProps.patient['Patient Global Notes'].length > 3,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { notesTooltipDisabled, notesTooltipHovered } = this.state;
    if (notesTooltipDisabled && !prevState.notesTooltipDisabled) {
      window.addEventListener('scroll', this.onScroll);
    }
    if (notesTooltipHovered && !prevState.notesTooltipHovered) {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  onNotesButtonMouseEnter() {
    this.setState({ notesTooltipHovered: true });
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus();
      }
    }, 100);
  }

  onNotesTooltipMouseEnter() {
    this.setState({ notesTooltipHovered: true });
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus();
      }
    }, 100);
  }

  onNotesButtonFocus() {
    this.setState({
      notesTooltipHovered: true,
    });
    if (this.buttonBlurTimeout) {
      clearTimeout(this.buttonBlurTimeout);
    }
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus();
      }
    }, 100);
  }

  onNotesTooltipMouseLeave() {
    this.setState({ notesTooltipHovered: false });
  }

  onNotesButtonMouseLeave() {
    this.setState({ notesTooltipHovered: false });
  }

  onOrderNotesSubmitSuccess(values, dispatch, props) {
    this.setState({ notesTooltipHovered: false });
    if (props && props.reset) {
      props.reset();
    }
    this.setState({
      filterOptions: {
        'Notes Category': uniqBy([
          ...this.state.filterOptions['Notes Category'],
          ...values['Notes Category'],
        ], 'ID'),
        'Author': uniqBy([
          ...this.state.filterOptions.Author,
          values.Author,
        ], 'ID'),
      }
    })
  }

  onOrderNotesSubmitButtonBlur() {
    this.setState({ notesTooltipHovered: false });
    if (this.filterInputRef) {
      this.filterInputRef.focus();
    }
  }

  onViewAllButtonClick() {
    this.setState({ trimmedView: false });
  }

  onNotesButtonClick() {
    if (!this.state.notesTooltipClicked) {
      document.addEventListener('click', this.onDocumentClick);
    } else {
      document.removeEventListener('click', this.onDocumentClick);
    }
    this.setState({ notesTooltipClicked: !this.state.notesTooltipClicked });
  }

  onDocumentClick(event) {
    if (!isDescendant(this.orderNotesRef, event.target)) {
      document.removeEventListener('click', this.onDocumentClick);
      this.setState({ notesTooltipClicked: false });
    }
  }

  onPrintButtonClick(notes) {
    const { patient, business } = this.props;
    const documentName = `${patient['First Name']}_${patient['Last Name']}_${patient['Date of Birth']}_Notes`;
    patientNotesToPDF({ patient, notes, business}).then(PDF => ({
      blob: PDF.output('blob'),
      Name: documentName,
    })).then((values) => {
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(values.blob, `${documentName}.pdf`);
      } else {
        printPdf(values.blob);
      }
    });
  }

  render() {
    const { patient, account, business, workflowStateFilter, classes, width } = this.props;
    const { trimmedView, notesTooltipHovered, notesTooltipClicked } = this.state;
    let filteredNotes;
    let notes;
    if (patient) {
      filteredNotes = workflowStateFilter.length > 0
        ? patient['Patient Global Notes'].filter(item => (workflowStateFilter.indexOf(null) !== -1
          && (item['Notes Category'].length === 0 || item['Notes Category'].filter(categoryItem => !categoryItem['Workflow State'].ID).length > 0))
          || item['Notes Category']
            .filter(categoryItem => workflowStateFilter.indexOf(categoryItem['Workflow State'].ID) !== -1).length > 0)
        : patient['Patient Global Notes'];
      notes = trimmedView
        ? filteredNotes.slice(0, 3)
        : filteredNotes;
    }
    const filterOptions = patient
        && filteredNotes
        && filteredNotes.length > 0
        ? filteredNotes.reduce((accumulator, currentValue) => ({
          'Notes Category': uniqBy([
            ...accumulator['Notes Category'],
            ...currentValue['Notes Category'],
          ], 'ID'),
          'Author': uniqBy([
            ...accumulator.Author,
            currentValue.Author,
          ], 'ID'),
        }), {
          'Notes Category': [],
          'Author': [],
        }) : {
          'Notes Category': [],
          'Author': [],
        };
    const NotesList = (
      <List className={classes.list}>
        {notes.map(notesItem => (
          <NotesItem
            key={notesItem.ID}
            business={business}
            onSubmitSuccess={this.onOrderNotesSubmitSuccess}
            {...notesItem}
          />
        ))}
      </List>
    );
    return (
      <Section>
        <SectionHeader>
          <SectionTitle>
            Patient Notes
          </SectionTitle>
          <div>
            <IconButton
              className={classes.iconButton}
              onClick={() => this.onPrintButtonClick(notes)}
            >
              <IconPrint className={classes.iconPrint} />
            </IconButton>
            <Tooltip
              className={classes.notesTooltip}
              popperClassName={classes.noteTooltipPopper}
              content={
                <NotesTooltipContent
                  {...{ 'Author ID': account.ID }}
                  {...patient ? { 'User Record Number': patient['Record Number'] } : {}}
                  onSubmitSuccess={this.onOrderNotesSubmitSuccess}
                  onSubmitButtonBlur={this.onOrderNotesSubmitButtonBlur}
                  rootRef={(orderNotesRef) => {
                    this.orderNotesRef = orderNotesRef;
                  }}
                  createInputRef={(createOrderNoteInputRef) => {
                    this.createOrderNoteInputRef = createOrderNoteInputRef;
                  }}
                  business={business}
                />
              }
              placement="left"
              disableHoverListener
              open={notesTooltipClicked ? true : notesTooltipHovered}
              disablePointerEvents={false}
              onMouseLeave={this.onNotesTooltipMouseLeave}
              onMouseEnter={this.onNotesTooltipMouseEnter}
            >
              <IconButton
                className={classNames(classes.iconButton, {
                  [classes.buttonNotesActive]: notesTooltipClicked ? true : notesTooltipHovered,
                })}
                onMouseOver={this.onNotesButtonMouseEnter}
                onMouseLeave={this.onNotesButtonMouseLeave}
                onFocus={this.onNotesButtonFocus}
                onClick={this.onNotesButtonClick}
              >
                <IconBlurb className={classes.iconBlurb} />
              </IconButton>
            </Tooltip>
          </div>
        </SectionHeader>
        <SectionBody
          padding="none"
          className={classNames({
            [classes.sectionBodyTrimmed]: trimmedView,
          })}
        >
          <NotesFilterForm
            form={`${patient['Record Number']}-notesFilter`}
            className={classes.notesFilterForm}
            filterInputRef={(filterInputRef) => {
              this.filterInputRef = filterInputRef;
            }}
            filterOptions={filterOptions}
          />
          {(notes && notes.length > 0) ? (
            <Fragment>
              {(width !== 'xs' && width !== 'sm') ? (
                <Scrollbars
                  autoHeight
                  autoHeightMin={0}
                  autoHeightMax={470}
                  thumbMinSize={30}
                  universal
                >
                  {NotesList}
                </Scrollbars>
              ) : (
                <Fragment>
                  {NotesList}
                </Fragment>
              )}
              {(trimmedView === true && filteredNotes && filteredNotes.length > 3) &&
                <SectionFooter>
                  <Link
                    color="primary"
                    bold
                    size="sm"
                    onClick={this.onViewAllButtonClick}
                  >
                    View all ({filteredNotes.length})
                  </Link>
                </SectionFooter>
              }
            </Fragment>
          ) : (
            <SectionBodyEmptyContent
              text="There are no comments"
            />
          )}
        </SectionBody>
        
      </Section>
    );
  }
}

Notes.propTypes = {
  patient: PropTypes.shape({
    'Record Number': PropTypes.number,
    'Patient Global Notes': PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number.isRequired,
    })).isRequired,
  }),
  account: PropTypes.shape({
    ID: PropTypes.number.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    submit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  classes: PropTypes.shape({
    notesForm: PropTypes.string.isRequired,
    notesFilterForm: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    iconPaperPlane: PropTypes.string.isRequired,
    sectionBodyTrimmed: PropTypes.string.isRequired,
    list: PropTypes.string.isRequired,
  }).isRequired,
};

Notes.defaultProps = {
  patient: null,
  business: {},
};

const mapStateToProps = (store, ownProps) => {
  const notesFilterValues = getFormValues(`${ownProps.patient['Record Number']}-notesFilter`)(store);
  const workflowStateFilter = [
    ...notesFilterValues
      && notesFilterValues.Intake ? [1] : [],
    ...notesFilterValues
      && notesFilterValues.Delivery ? [2] : [],
    ...notesFilterValues
      && notesFilterValues.Billing ? [3] : [],
    ...notesFilterValues
      && notesFilterValues.Collection ? [4] : [],
    ...notesFilterValues
      && notesFilterValues.Paid ? [5] : [],
    ...notesFilterValues
      && notesFilterValues.General ? [null] : [],
  ];
  return {
    account: store.account,
    workflowStateFilter,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ submit, reset }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(withStyles(styles)(Notes)),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/Notes/Notes.js